/**
 * useMissions Hook
 * Zustand store for mission state management
 * Fetches missions from orchestration-service API
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { useAuth } from '@agion/auth'
import { missionsApi, type MissionResponse } from '../services/api'
import type { CancellableRequest } from '../services/api-client'
import type { Mission, MissionParticipant } from '../types/mission'
import { declaresChatMessage, parseLaunchInputs } from '../utils/launchInputs'

interface MissionsStore {
  // State
  missions: Mission[]
  isLoading: boolean
  error: string | null
  /** The failed read itself, for screens that word it from its typed fields (ADR-20260925). */
  failure: unknown
  lastFetched: Date | null
  /** True while `missions` holds every page of the last load; false until one
   *  succeeds and after a failed one, so nothing is judged missing from a
   *  partial list. */
  complete: boolean

  // Actions
  loadMissions: () => Promise<void>
  /** options.runReadiness asks the server for run_readiness (the new chat
   *  only, persona F-CHAT-08); without it the read is the current chat's. */
  loadMissionDetails: (missionId: string, options?: DetailReadOptions) => Promise<Mission | undefined>
  getMissionById: (missionId: string) => Mission | undefined
  getMissionAgents: (missionId: string) => MissionParticipant[]
  clearError: () => void
}

/** The observable signed-in identity; session revalidation alone does not change it. */
export function missionViewerIdentity(state: ReturnType<typeof useAuth.getState>): string {
  return JSON.stringify([state.user?.id, state.user?.organizationId, state.isAuthenticated,
    state.sessionData?.userId, state.sessionData?.organizationId, state.sessionData?.authenticated])
}

let currentDetailRequest: CancellableRequest<MissionResponse> | undefined

/** What a mission detail read asks the server for beyond the mission itself. */
export interface DetailReadOptions {
  runReadiness?: boolean
}

/** The mission with its current-viewer answers withdrawn while a read is in
 *  flight or after it failed; run readiness only when the read asks for it. */
function withdrawAnswers(mission: Mission, runReadiness: boolean): Mission {
  return runReadiness ? { ...mission, canStart: undefined, runReadiness: undefined } : { ...mission, canStart: undefined }
}

/** Missions per list request; the lightweight list answers 200 in about 50 ms. */
export const MISSION_LIST_PAGE_SIZE = 200

/**
 * Every mission GET /v1/missions returns for the caller (agion/Agion#2873).
 * Pages with limit and offset until the server's total; advances by the rows
 * received, since the server may clamp the limit. Without a total it stops at
 * a short page, and a page that brings no new mission ends the read so a
 * server that ignores the offset cannot loop it. A mission that a creation
 * during the read pushes onto the next page is kept once.
 */
async function listAllMissions(): Promise<MissionResponse[]> {
  const byId = new Map<string, MissionResponse>()
  let offset = 0
  for (;;) {
    const response = await missionsApi.listMissionsPage(offset, MISSION_LIST_PAGE_SIZE)
    const page = response.missions ?? []
    const before = byId.size
    for (const item of page) if (!byId.has(item.id)) byId.set(item.id, item)
    offset += page.length
    if (page.length === 0 || byId.size === before) break
    if (typeof response.total === 'number' ? offset >= response.total : page.length < MISSION_LIST_PAGE_SIZE) break
  }
  return Array.from(byId.values())
}

/**
 * Convert API response to Mission type
 * V2 API returns agents and members as separate arrays.
 * We combine them into a unified participants array for the frontend.
 *
 * NOTE: We keep 'agent' as-is instead of converting to 'ai'.
 * This aligns with the unified mission architecture and OpenFGA model.
 */
export function mapMissionResponse(response: MissionResponse): Mission {
  // Build participants from v2 agents and members arrays
  const participants: MissionParticipant[] = []

  // Map agents (v2 format)
  if (response.agents && response.agents.length > 0) {
    for (const agent of response.agents) {
      participants.push({
        id: agent.id,
        actorId: agent.agent_id,
        actorType: 'agent',
        displayName: agent.display_name || agent.agent_name || agent.agent_id,
        role: (agent.role || 'member') as MissionParticipant['role']
      })
    }
  }

  // Map members (v2 format - human users)
  if (response.members && response.members.length > 0) {
    for (const member of response.members) {
      participants.push({
        id: member.id,
        actorId: member.user_id,
        actorType: 'human',
        displayName: member.display_name || member.email || member.user_id,
        role: member.role as MissionParticipant['role']
      })
    }
  }

  // Fallback to legacy participants array if v2 arrays are empty
  if (participants.length === 0 && response.participants) {
    for (const p of response.participants) {
      participants.push({
        id: p.id,
        actorId: p.id,
        actorType: p.type as MissionParticipant['actorType'],
        displayName: p.display_name || p.id,
        role: (p.role || 'member') as MissionParticipant['role']
      })
    }
  }

  // Calculate agent count from v2 response or from participants
  const agentCount = response.agent_count ?? participants.filter(p => p.actorType === 'agent').length

  // Agent name map: node_id/agent_uuid → display label
  // Prefer the backend-computed agent_names map (available on single mission fetch)
  // which indexes both node IDs and governance agent UUIDs.
  // Falls back to building from agents[] array for older API responses.
  let agentNameMap: Record<string, string> = {}
  // keysToCamel converts agent_names → agentNames at runtime
  const backendAgentNames = (response as any).agentNames ?? (response as any).agent_names
  if (backendAgentNames) {
    agentNameMap = backendAgentNames as Record<string, string>
  } else if (response.agents && response.agents.length > 0) {
    for (const agent of response.agents) {
      if (agent.agent_name && agent.display_name) {
        agentNameMap[agent.agent_name] = agent.display_name
      }
      if (agent.agent_id && agent.display_name) {
        agentNameMap[agent.agent_id] = agent.display_name
      }
    }
  }

  // Participant name map: user_id → display name (for multi-user chat)
  // keysToCamel converts participant_names → participantNames at runtime
  const participantNames: Record<string, string> = (response as any).participantNames ?? (response as any).participant_names ?? {}

  // Extract host info from response (if mission is bound to an Edge host).
  // F16 (chat-ui catch-up): the host_type check was dead — the
  // orchestration-service Mission model (missions/models.go:273) only has
  // host_id, never host_type. Edge routing is now signalled by host_id presence.
  const hostId = (response as any).host_id as string | undefined
  const hostInfo = hostId ? {
    hostType: 'byoh' as const,
    hostId,
    hostEndpoint: (response as any).host_endpoint,
  } : undefined

  // Detect voice capability from the canonical workflow definition (legacy metadata.workflow fallback)
  const metadata = (response as any).metadata
  const workflowDefinition = (response as any).workflow_definition ?? (response as any).workflowDefinition
  let hasVoiceCapability = false
  const workflowSource = workflowDefinition ?? metadata?.workflow
  if (workflowSource) {
    try {
      const wf = typeof workflowSource === 'string'
        ? JSON.parse(workflowSource)
        : workflowSource
      if (wf?.metadata?.capabilities?.modality === 'voice') {
        hasVoiceCapability = true
      }
      if (!hasVoiceCapability && Array.isArray(wf?.nodes)) {
        hasVoiceCapability = wf.nodes.some((n: any) =>
          n?.agentConfig?.interactionChannel === 'voice' ||
          n?.agentConfig?.interactionChannel === 'voice-outbound'
        )
      }
    } catch {
      // Ignore parse errors
    }
  }
  // Fallback: check agent capabilities array
  if (!hasVoiceCapability) {
    hasVoiceCapability = response.agents?.some(
      a => a.capabilities?.includes('voice')
    ) ?? false
  }

  const runReadiness = response.runReadiness ?? response.run_readiness
  // Start values a new run asks for (agion/Agion#2413)
  const rawLaunchInputs = response.launch_inputs ?? (response as any).launchInputs
  const launchInputs = parseLaunchInputs(rawLaunchInputs)

  return {
    id: response.id,
    name: response.name || 'Unnamed Mission',
    description: response.description,
    executionMode: (response as any).execution_mode as Mission['executionMode'],
    status: response.status as Mission['status'],
    state: response.status as Mission['status'],
    priority: undefined,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
    participants,
    agentCount,
    hasVoiceCapability: hasVoiceCapability || undefined,
    agentNameMap: Object.keys(agentNameMap).length > 0 ? agentNameMap : undefined,
    participantNames: Object.keys(participantNames).length > 0 ? participantNames : undefined,
    launchInputs: launchInputs.length > 0 ? launchInputs : undefined,
    launchDeclaresMessage: declaresChatMessage(rawLaunchInputs) || undefined,
    canStart: response.canStart ?? response.can_start,
    // Only a read that asked for run_readiness carries it (the new chat).
    ...(runReadiness ? { runReadiness } : {}),
    // Orchestrator agent ID (available for all mission types)
    primaryAgentId: response.orchestrator_id,
    // BYOH host info
    ...hostInfo,
    hostGroupId: ((response as any).host_group_id as string | undefined) || undefined,
    // Include metadata for MissionBindings agent lookup
    metadata: (response as any).metadata,
  } as Mission
}

export const useMissionStore = create<MissionsStore>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    missions: [],
    isLoading: false,
    error: null,
    failure: null,
    lastFetched: null,
    complete: false,

    // Actions
    loadMissions: async () => {
      // Prevent duplicate loading
      if (get().isLoading) return

      set({ isLoading: true, error: null, failure: null })

      try {
        const listed = await listAllMissions()
        // The list carries no launch_inputs: a mission keeps the start values
        // its details already loaded (agion/Agion#2413). Nor run_readiness: it
        // keeps the server's answer its details loaded (F-CHAT-08).
        const loaded = get().missions
        const missions = listed.map((item) => {
          const mission = mapMissionResponse(item)
          if (!('launch_inputs' in item) && !('launchInputs' in item)) {
            const known = loaded.find((m) => m.id === mission.id)
            if (known?.launchInputs) mission.launchInputs = known.launchInputs
            if (known?.launchDeclaresMessage) mission.launchDeclaresMessage = true
          }
          if (!('run_readiness' in item) && !('runReadiness' in item)) {
            const runReadiness = loaded.find((m) => m.id === mission.id)?.runReadiness
            if (runReadiness) mission.runReadiness = runReadiness
          }
          return mission
        })

        console.log('[useMissions] Loaded missions:', missions.length)
        set({
          missions,
          isLoading: false,
          lastFetched: new Date(),
          complete: true,
        })
      } catch (error) {
        console.error('[useMissions] Failed to load missions:', error)
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load missions',
          failure: error,
          complete: false,
        })
      }
    },

    loadMissionDetails: async (missionId: string, options?: DetailReadOptions) => {
      currentDetailRequest?.abort()
      const identity = missionViewerIdentity(useAuth.getState())
      const asksRunReadiness = options?.runReadiness === true
      const request = asksRunReadiness ? missionsApi.getMissionCancellable(missionId, { runReadiness: true }) : missionsApi.getMissionCancellable(missionId)
      currentDetailRequest = request
      set((state) => ({ missions: state.missions.map((mission) => mission.id === missionId ? withdrawAnswers(mission, asksRunReadiness) : mission) }))
      const isCurrent = () => currentDetailRequest === request && identity === missionViewerIdentity(useAuth.getState())
      try {
        const response = await request.promise
        if (!isCurrent()) return undefined
        const mission = mapMissionResponse(response)
        // A read that did not ask keeps the answer an asking read stored.
        if (!asksRunReadiness) {
          const kept = get().missions.find((m) => m.id === missionId)?.runReadiness
          if (kept) mission.runReadiness = kept
        }

        console.log('[useMissions] Loaded mission details:', missionId, {
          participants: mission.participants?.length || 0,
          agentCount: mission.agentCount,
          agentNameMap: mission.agentNameMap,
          participantNames: mission.participantNames,
        })

        // Update the mission in the store (or insert if not present)
        set((state) => {
          const exists = state.missions.some(m => m.id === missionId)
          return {
            missions: exists
              ? state.missions.map(m => m.id === missionId ? mission : m)
              : [...state.missions, mission],
          }
        })

        return mission
      } catch (error) {
        if (!isCurrent()) return undefined
        console.error('[useMissions] Failed to load mission details:', missionId, error)
        set((state) => ({ missions: state.missions.map((mission) => mission.id === missionId ? withdrawAnswers(mission, asksRunReadiness) : mission) }))
        return undefined
      } finally {
        if (currentDetailRequest === request) currentDetailRequest = undefined
      }
    },

    getMissionById: (missionId: string) => {
      return get().missions.find(m => m.id === missionId)
    },

    getMissionAgents: (missionId: string) => {
      const mission = get().missions.find(m => m.id === missionId)
      if (!mission?.participants) return []
      // Filter by 'agent' (not 'ai' - that was legacy)
      return mission.participants.filter(p => p.actorType === 'agent')
    },

    clearError: () => {
      set({ error: null })
    }
  }))
)

const unsubscribeIdentity = useAuth.subscribe((state, previous) => {
  if (missionViewerIdentity(state) === missionViewerIdentity(previous)) return
  currentDetailRequest?.abort()
  currentDetailRequest = undefined
  useMissionStore.setState((stored) => ({ missions: stored.missions.map((mission) => ({ ...mission, canStart: undefined, runReadiness: undefined })) }))
})
if (import.meta.hot) import.meta.hot.dispose(unsubscribeIdentity)

// Hook alias for convenience
export const useMissions = useMissionStore
